// Settings → Therapist location window (0394).
//
// A therapist is only given a Book Now booking while her phone has sent a
// location recently. The partner app is a web app, so a locked phone sends
// nothing, although push notifications still reach it. 5 minutes is the
// approved value (TEND-027); a longer window lets her lock her phone while she
// waits on duty. Riders are not affected.

import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { type OperatingSettings, useUpdateOperatingSettings } from "./settings.hooks";
import { Field, NumberInput } from "./settingsFields";

const APPROVED_WINDOW_MINUTES = 5;

export function TherapistLocationWindowCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [draft, setDraft] = useState(() => String(settings.therapist_location_window_minutes));

  useEffect(() => {
    setDraft(String(settings.therapist_location_window_minutes));
  }, [settings]);

  const parsed = Number(draft);
  // The database refuses outside 5–240 (0394); checked here so the reason is
  // readable before the round trip.
  const valid = Number.isInteger(parsed) && parsed >= 5 && parsed <= 240;

  function save() {
    if (!valid) {
      toast.error("The location window has to be a whole number of minutes, 5 to 240.");
      return;
    }
    update.mutate(
      { therapist_location_window_minutes: parsed },
      {
        onSuccess: () => toast.success("Therapist location window saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <Card className="gap-5">
      <CardHeader className="gap-2">
        <CardTitle className="type-title-body">
          <h2>Therapist location window</h2>
        </CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          How long a therapist on duty still gets Book Now bookings after her phone last sent its
          location. Her phone only sends it while the partner app is open, but push notifications
          reach a locked phone. When she opens the booking, her live location updates. Riders are
          not affected.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-5">
        <Field label="Window (minutes, 5–240)">
          <NumberInput value={draft} onChange={setDraft} />
        </Field>
        {parsed !== APPROVED_WINDOW_MINUTES && valid ? (
          <p className="max-w-prose text-sm leading-relaxed text-warning">
            {APPROVED_WINDOW_MINUTES} minutes is the approved window. {parsed} minutes is a
            deliberate exception: a therapist whose phone died or who left can still be given a
            booking for up to {parsed} minutes.
          </p>
        ) : null}
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={update.isPending || !valid}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}
